import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Spin, Tabs, Table, Button, Space, Popconfirm, Tag, Select as AntSelect } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { Field, Form } from 'react-final-form'
import useAction from 'utils/useAction'
import {
  fetchNetworkProgram,
  createNetworkProgram,
  updateNetworkProgram,
  deleteNetworkProgramGoal,
  createNetworkProgramFeature,
  deleteNetworkProgramFeature,
} from 'actions/views/earnOffersManagement'
import { openKarmaEarnGoalModal } from 'reducers/modals/actions'
import { selectors as earnOffersSelectors } from 'domains/earnOffersManagement'
import Input from 'components/Input/Input'
import InputNumber from 'components/Input/InputNumber'
import Select from 'components/Select/Select'
import MultipleSelect from 'components/Select/MultipleSelect'
import TextArea from 'components/TextArea/TextArea'
import ImagePicker from 'components/ImagePicker'
import { required } from 'helpers/validators'
import { DOMAIN_TYPE_OPTIONS, PROGRAM_STATUS_OPTIONS } from 'const/earnPrograms'
import { goalColumns } from 'pages/EarnOffersManagement/columns'
import {
  StyledForm,
  SectionTitle,
  ImagesRow,
  ImageField,
  ImageLabel,
  Footer,
  StyledButton,
} from './KarmaEarnProgram.styles'

const { TabPane } = Tabs

const GoalsTab = ({ programId }) => {
  const getRowById = useSelector(earnOffersSelectors.getProgramRowById)
  const openGoalModal = useAction(openKarmaEarnGoalModal)
  const deleteGoal = useAction(deleteNetworkProgramGoal)

  const goals = getRowById(programId)?.goals || []

  const columns = [
    ...goalColumns,
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      render: (_, goal) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openGoalModal({ id: goal.id, networkProgramId: programId })}
          />
          <Popconfirm
            title="Delete this goal?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteGoal({ id: goal.id })}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <>
      <div style={{ marginBottom: 12, textAlign: 'right' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openGoalModal({ networkProgramId: programId })}
        >
          Add Goal
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={goals}
        rowKey={(goal) => goal.id}
        pagination={false}
        size="small"
        locale={{ emptyText: 'No goals' }}
      />
    </>
  )
}

GoalsTab.propTypes = {
  programId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
}

// API genre labels arrive as lowercase enum names, e.g. "action_adventure"
const prettifyGenre = (label) =>
  String(label || '')
    .split('_')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ')

const FeaturesTab = ({ programId, features }) => {
  const createFeature = useAction(createNetworkProgramFeature)
  const deleteFeature = useAction(deleteNetworkProgramFeature)
  const isFeatureSaving = useSelector(earnOffersSelectors.getIsFeatureSaving)
  const gameGenres = useSelector(earnOffersSelectors.getGameGenres)
  const [genreToAdd, setGenreToAdd] = useState(undefined)

  const usedValues = features.map((feature) => feature.feature_value)
  const available = gameGenres
    .filter((genre) => !usedValues.includes(genre.value))
    .map((genre) => ({ value: genre.value, label: prettifyGenre(genre.label) }))

  const handleAdd = () => {
    if (genreToAdd === undefined || genreToAdd === null) return
    createFeature({
      data: {
        network_program_id: programId,
        feature_type: 'GAME_GENRE',
        feature_value: genreToAdd,
      },
      networkProgramId: programId,
    })
    setGenreToAdd(undefined)
  }

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        {features.length === 0 ? (
          <span>No genres yet.</span>
        ) : (
          <Space wrap>
            {features.map((feature) => (
              <Popconfirm
                key={feature.id}
                title="Remove this genre?"
                okText="Remove"
                okButtonProps={{ danger: true }}
                onConfirm={() =>
                  deleteFeature({ id: feature.id, networkProgramId: programId })
                }
              >
                <Tag
                  closable
                  onClose={(e) => e.preventDefault()}
                  style={{ cursor: 'pointer' }}
                >
                  {prettifyGenre(feature.feature_label)}
                </Tag>
              </Popconfirm>
            ))}
          </Space>
        )}
      </div>
      <Space>
        <AntSelect
          style={{ width: 240 }}
          placeholder="Select a genre to add"
          options={available}
          value={genreToAdd}
          onChange={setGenreToAdd}
          disabled={available.length === 0}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          loading={isFeatureSaving}
          disabled={genreToAdd === undefined || isFeatureSaving}
        >
          Add
        </Button>
      </Space>
    </>
  )
}

FeaturesTab.propTypes = {
  programId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  features: PropTypes.arrayOf(PropTypes.object),
}

FeaturesTab.defaultProps = {
  features: [],
}

// Mirrors the KarmaEarn games mapper rule (GameOfferMapper REPLACE_TOKEN, param "uid"):
// the URL must carry `uid={uid}` as a query parameter, not just contain `{uid}`.
const UID_QUERY_PARAM_REGEX = /(^|[?&])uid=\{uid\}([&#]|$)/
const validTrackingUrl = (value) => {
  if (!value) return undefined
  if (!/^https:\/\//.test(value)) {
    return 'Must be an absolute https:// URL'
  }
  if (!UID_QUERY_PARAM_REGEX.test(value)) {
    return 'URL must contain the uid={uid} query parameter, e.g. ?uid={uid}'
  }
  return undefined
}

// Map a single image form field to its request key, honouring the "null leaves
// unchanged / empty string clears / data-uri replaces" contract.
const applyImage = (payload, current, original, requestKey) => {
  if (current && current.startsWith('data:')) {
    payload[requestKey] = current // newly uploaded image
  } else if (!current && original) {
    payload[requestKey] = '' // existing image was removed
  }
  // unchanged → omit so the server leaves it as-is
}

const OPTIONAL_PRUNE_KEYS = [
  'subtitle',
  'description',
  'tracking_url',
  'currency',
  'amount',
  'cpi',
  'commission',
  'min_android_version',
  'max_android_version',
  'min_ios_version',
  'max_ios_version',
]

const KarmaEarnProgram = ({ id, closeModal }) => {
  const fetchProgram = useAction(fetchNetworkProgram)
  const createProgram = useAction(createNetworkProgram)
  const updateProgram = useAction(updateNetworkProgram)

  const detail = useSelector(earnOffersSelectors.getProgramDetail)
  const isLoading = useSelector(earnOffersSelectors.getIsProgramLoading)
  const isSaving = useSelector(earnOffersSelectors.getIsProgramSaving)

  useEffect(() => {
    if (id) {
      fetchProgram({ id })
    }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const apiData = id && detail ? detail.api_data || {} : {}

  const initialValues = useMemo(() => {
    if (!id) {
      // Note: only ACCEPTED programs get referral links (NetworkProgramDao.findByIds
      // filters ACCEPTED); a pending program shows "program not found" until accepted.
      return { type: 'GAME', program_status: 'pending' }
    }
    return {
      type: detail?.type || 'GAME',
      domain: detail?.domain,
      website_id: detail?.website_id,
      program_id: detail?.program_id,
      program_status: detail?.program_status,
      name: detail?.name,
      subtitle: apiData.subtitle,
      description: apiData.description,
      tracking_url: apiData.tracking_url,
      currency: apiData.currency,
      amount: apiData.amount,
      cpi: apiData.cpi,
      commission: apiData.commission,
      categories: apiData.categories || [],
      terms: apiData.terms || [],
      countries: apiData.countries || [],
      min_android_version: apiData.min_android_version,
      max_android_version: apiData.max_android_version,
      min_ios_version: apiData.min_ios_version,
      max_ios_version: apiData.max_ios_version,
      icon_image: apiData.icon_url,
      large_icon_image: apiData.large_icon_url,
      banner_image: apiData.banner_url,
    }
  }, [id, detail]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = (values) => {
    const {
      icon_image: iconImage,
      large_icon_image: largeIconImage,
      banner_image: bannerImage,
      ...rest
    } = values

    const payload = { ...rest }

    // domains must be normalized lowercase to match across networks
    if (payload.domain) {
      payload.domain = payload.domain.trim().toLowerCase()
    }

    OPTIONAL_PRUNE_KEYS.forEach((key) => {
      if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
        delete payload[key]
      }
    })
    ;['categories', 'terms', 'countries'].forEach((key) => {
      if (!payload[key] || payload[key].length === 0) {
        delete payload[key]
      }
    })

    applyImage(payload, iconImage, apiData.icon_url, 'icon_image_data_uri')
    applyImage(payload, largeIconImage, apiData.large_icon_url, 'large_icon_image_data_uri')
    applyImage(payload, bannerImage, apiData.banner_url, 'banner_image_data_uri')

    if (id) {
      updateProgram({ id, data: payload, callback: closeModal })
    } else {
      createProgram({ data: payload, callback: closeModal })
    }
  }

  if (id && (isLoading || !detail)) {
    return (
      <Spin spinning size="large">
        <div style={{ height: 120 }} />
      </Spin>
    )
  }

  const detailsForm = (
    <Spin spinning={isSaving} size="large">
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting }) => (
          <StyledForm onSubmit={handleSubmit}>
            <SectionTitle>Core</SectionTitle>
            <Field
              required
              name="type"
              label="Type"
              options={DOMAIN_TYPE_OPTIONS}
              component={Select}
              validate={required}
            />
            <Field
              required
              name="domain"
              label="Domain"
              description="Identifier without the type prefix, e.g. my-test-offer"
              component={Input}
              validate={required}
            />
            <Field
              required
              name="website_id"
              label="Platform (website_id)"
              description="e.g. android"
              component={Input}
              validate={required}
            />
            <Field
              required
              name="program_id"
              label="Program ID"
              description="External program ID"
              component={Input}
              validate={required}
            />
            <Field
              required
              name="program_status"
              label="Program Status"
              options={PROGRAM_STATUS_OPTIONS}
              component={Select}
              validate={required}
            />
            <Field
              required
              name="name"
              label="Name"
              component={Input}
              validate={required}
            />

            <SectionTitle>Details</SectionTitle>
            <Field name="subtitle" label="Subtitle" component={Input} />
            <Field name="description" label="Description" component={TextArea} />
            <Field
              name="tracking_url"
              label="Tracking URL"
              description="https://… with uid={uid}, e.g. https://track.partner.com/click?uid={uid}"
              component={Input}
              validate={validTrackingUrl}
            />
            <Field name="currency" label="Currency" description="e.g. USD" component={Input} />
            <Field name="amount" label="Amount" min={0} component={InputNumber} />
            <Field name="cpi" label="CPI" min={0} component={InputNumber} />
            <Field name="commission" label="Commission" min={0} component={InputNumber} />
            <Field name="categories" label="Categories" component={MultipleSelect} />
            <Field name="terms" label="Terms" component={MultipleSelect} />
            <Field name="countries" label="Countries" component={MultipleSelect} />
            <Field name="min_android_version" label="Min Android Version" component={Input} />
            <Field name="max_android_version" label="Max Android Version" component={Input} />
            <Field name="min_ios_version" label="Min iOS Version" component={Input} />
            <Field name="max_ios_version" label="Max iOS Version" component={Input} />

            <SectionTitle>Images</SectionTitle>
            <ImagesRow>
              <ImageField>
                <ImageLabel>Icon</ImageLabel>
                <Field name="icon_image" component={ImagePicker} />
              </ImageField>
              <ImageField>
                <ImageLabel>Large Icon</ImageLabel>
                <Field name="large_icon_image" component={ImagePicker} />
              </ImageField>
              <ImageField>
                <ImageLabel>Banner</ImageLabel>
                <Field name="banner_image" component={ImagePicker} />
              </ImageField>
            </ImagesRow>

            <Footer>
              <StyledButton onClick={closeModal}>Cancel</StyledButton>
              <StyledButton
                type="primary"
                onClick={handleSubmit}
                loading={isSaving}
                disabled={submitting || isSaving}
              >
                {id ? 'Save changes' : 'Create program'}
              </StyledButton>
            </Footer>
          </StyledForm>
        )}
      />
    </Spin>
  )

  if (!id) {
    return detailsForm
  }

  return (
    <Tabs defaultActiveKey="details">
      <TabPane tab="Details" key="details">
        {detailsForm}
      </TabPane>
      <TabPane tab="Goals" key="goals">
        <GoalsTab programId={id} />
      </TabPane>
      <TabPane tab="Genres" key="genres">
        <FeaturesTab programId={id} features={detail?.features || []} />
      </TabPane>
    </Tabs>
  )
}

KarmaEarnProgram.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  closeModal: PropTypes.func.isRequired,
}

KarmaEarnProgram.defaultProps = {
  id: null,
}

export default KarmaEarnProgram
